/**
 * additional_dialog_info 上の joblistKeywordTiers / joblistPrefs から
 * キーワード・エリアを解決する汎用ヘルパー（workgate 行以外でも利用可）。
 */
import workgateConditionRow from '@/assets/rt/workgate-condition-row.json'
import { parseAdditionalDialogInfo } from '@/plugins/rt-dialog-migration'

export type JoblistKeywordTiers = string[][]

export type KeywordCheckFn = (
  formatId: string,
  formatData: object | null | undefined,
  keyword: string
) => boolean

export type KeywordCheck3Fn = (
  formatData: object | null | undefined,
  keyword: string
) => boolean

type WorkgateRowJson = {
  additional_dialog_info?: {
    joblistKeywordTiers?: unknown
    joblistPrefs?: unknown
  }
}

const defaultAdd = (workgateConditionRow as WorkgateRowJson).additional_dialog_info || {}

/** workgate-condition-row.json を単一ソースとしたデフォルト tier */
export const JOBLIST_KEYWORD_TIERS_DEFAULT: JoblistKeywordTiers =
  normalizeJoblistKeywordTiers(defaultAdd.joblistKeywordTiers)

/** workgate-condition-row.json を単一ソースとしたデフォルト prefs */
export const JOBLIST_PREFS_DEFAULT: string[] =
  normalizeJoblistPrefs(defaultAdd.joblistPrefs)

export function isWorkgateConditionRow (row: unknown): boolean {
  if (!row || typeof row !== 'object') return false
  const r = row as { dialog_name?: unknown, dialog_type?: unknown }
  return r.dialog_name === 'workgate' ||
    r.dialog_type === 'open-workgate-popup-dialog'
}

export function normalizeJoblistKeywordTiers (value: unknown): JoblistKeywordTiers {
  if (!Array.isArray(value)) return []
  return value
    .map((tier) => {
      if (!Array.isArray(tier)) return []
      return tier.filter((kw): kw is string => typeof kw === 'string' && kw !== '')
    })
    .filter((tier) => tier.length > 0)
}

export function normalizeJoblistPrefs (value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((p): p is string => typeof p === 'string' && p !== '')
}

export function getJoblistKeywordTiers (additionalDialogInfo: unknown): JoblistKeywordTiers {
  const add = parseAdditionalDialogInfo(additionalDialogInfo)
  const tiers = normalizeJoblistKeywordTiers(add.joblistKeywordTiers)
  return tiers.length > 0 ? tiers : JOBLIST_KEYWORD_TIERS_DEFAULT
}

export function getJoblistPrefs (additionalDialogInfo: unknown): string[] {
  const add = parseAdditionalDialogInfo(additionalDialogInfo)
  const prefs = normalizeJoblistPrefs(add.joblistPrefs)
  return prefs.length > 0 ? prefs : JOBLIST_PREFS_DEFAULT
}

export function resolveJoblistKeyword (params: {
  formatId: string
  formatData: object | null | undefined
  tiers: JoblistKeywordTiers
  keywordCheck: KeywordCheckFn
  keywordCheck3: KeywordCheck3Fn
}): string {
  const { formatId, formatData, tiers, keywordCheck, keywordCheck3 } = params
  const isShokumu = formatId === 'shokumukeirekisho'
  const fd = (formatData || {}) as { hopeJobcategory?: unknown }

  const hopeJob = fd.hopeJobcategory
  if (hopeJob) {
    const hopeStr = Array.isArray(hopeJob) ? hopeJob.join('') : String(hopeJob)
    for (let t = 0; t < tiers.length; t++) {
      for (let i = 0; i < tiers[t].length; i++) {
        if (hopeStr.indexOf(tiers[t][i]) >= 0) return tiers[t][i]
      }
    }
  }

  for (let t = 0; t < tiers.length; t++) {
    const tier = tiers[t]
    for (let i = 0; i < tier.length; i++) {
      const keyword = tier[i]
      const hit = isShokumu
        ? keywordCheck3(formatData, keyword)
        : keywordCheck(formatId, formatData, keyword)
      if (hit) return keyword
    }
  }
  return ''
}

export function resolveJoblistPref (params: {
  basicData: { address1?: string } | null | undefined
  prefs: string[]
}): string {
  const address1 = params.basicData && params.basicData.address1
    ? String(params.basicData.address1)
    : ''
  let pref = ''
  for (let i = 0; i < params.prefs.length; i++) {
    if (address1.indexOf(params.prefs[i]) >= 0) {
      pref = params.prefs[i]
    }
  }
  return pref
}

export function resolveWorkgateOpenArgs (
  row: { additional_dialog_info?: unknown } | null | undefined,
  ctx: {
    formatId: string
    formatData: object | null | undefined
    basicData: { address1?: string } | null | undefined
    keywordCheck: KeywordCheckFn
    keywordCheck3: KeywordCheck3Fn
  }
): { keyword: string, area: string } {
  const add = row && row.additional_dialog_info
  const tiers = getJoblistKeywordTiers(add)
  const prefs = getJoblistPrefs(add)
  return {
    keyword: resolveJoblistKeyword({
      formatId: ctx.formatId,
      formatData: ctx.formatData,
      tiers,
      keywordCheck: ctx.keywordCheck,
      keywordCheck3: ctx.keywordCheck3
    }),
    area: resolveJoblistPref({
      basicData: ctx.basicData,
      prefs
    })
  }
}
